import { useState } from 'react';
import { Sheet } from '../components/Sheet.jsx';
import { Icon } from '../components/Icon.jsx';
import { useDeleteLeistung, useUpdateLeistung } from '../hooks/data.js';
import { useToast } from '../state/toast.jsx';
import { dayLabel, hours } from '../lib/format.js';
import { dauerAendern } from '../lib/kopieren.js';

const DAUERN = [0.25, 0.5, 1, 1.5, 2, 3, 4, 6, 8];

/**
 * Einen Zeiteintrag korrigieren, löschen oder kopieren.
 * Ändern nur eigene Einträge; gesperrte (rapportiert/abgerechnet) sind nur lesbar – Exxas würde die Änderung ablehnen.
 * Kopieren geht immer (die Kopie ist eine neue Buchung auf mich).
 */
export function LeistungSheet({ nr, leistung, onClose, onKopieren }) {
  const update = useUpdateLeistung(nr);
  const remove = useDeleteLeistung(nr);
  const toast = useToast();
  const [stunden, setStunden] = useState(leistung.stunden);
  const [beschreibung, setBeschreibung] = useState(leistung.beschreibung ?? '');
  const [error, setError] = useState('');
  const busy = update.isPending || remove.isPending;
  const dauern = DAUERN.includes(leistung.stunden) ? DAUERN : [...DAUERN, leistung.stunden].sort((a, b) => a - b);

  const save = async () => {
    setError('');
    try {
      await update.mutateAsync({ id: leistung.id, stunden, beschreibung: beschreibung.trim() });
      toast({ text: `Zeit korrigiert: ${hours(stunden)}` });
      onClose();
    } catch (err) { setError(err.message); }
  };

  const del = async () => {
    setError('');
    try {
      await remove.mutateAsync(leistung.id);
      toast({ text: `${hours(leistung.stunden)} von ${leistung.mitarbeiter} gelöscht` });
      onClose();
    } catch (err) { setError(err.message); }
  };

  return (
    <Sheet title="Zeiteintrag" onClose={onClose}>
      <span className="sub">{dayLabel(leistung.datum?.slice(0, 10))} · {leistung.mitarbeiter}{leistung.leistungsart ? ` · ${leistung.leistungsart}` : ''}</span>
      {!leistung.meine ? (
        <>
          <div className="notice info" role="status">
            <Icon name="users" size={18} />
            <span>{hours(leistung.stunden)} {leistung.beschreibung} gehört {leistung.mitarbeiter}. Ändern kann nur, wer die Zeit gebucht hat.</span>
          </div>
          <button className="btn lg block" onClick={() => onKopieren(leistung)}><Icon name="plus" />Für mich übernehmen</button>
          <button className="btn secondary block" onClick={onClose}>Schliessen</button>
        </>
      ) : leistung.gesperrt ? (
        <>
          <div className="notice" role="status">
            <Icon name="lock" size={18} stroke={2.2} />
            <span>{hours(leistung.stunden)} {leistung.beschreibung} ist bereits rapportiert oder abgerechnet und kann in der App nicht mehr geändert werden. Korrekturen bitte über das Büro.</span>
          </div>
          <button className="btn lg block" onClick={() => onKopieren(leistung)}><Icon name="plus" />Kopieren</button>
          <button className="btn secondary block" onClick={onClose}>Schliessen</button>
        </>
      ) : (
        <>
          <div className="stack" style={{ gap: 8 }}>
            <span className="label">Dauer</span>
            <div className="row" style={{ gap: 10 }}>
              <button type="button" className="btn secondary" style={{ minHeight: 'var(--tap-lg)', flex: 1 }} onClick={() => setStunden((h) => dauerAendern(h, -0.5))} disabled={stunden <= 0.25} aria-label="Eine halbe Stunde weniger">−0:30</button>
              <span className="display-zahl" style={{ fontSize: 28, fontWeight: 760, minWidth: 96, textAlign: 'center' }} aria-live="polite">{hours(stunden)}</span>
              <button type="button" className="btn secondary" style={{ minHeight: 'var(--tap-lg)', flex: 1 }} onClick={() => setStunden((h) => dauerAendern(h, 0.5))} disabled={stunden >= 24} aria-label="Eine halbe Stunde mehr">+0:30</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 6 }}>
              {dauern.map((h) => (
                <button key={h} className={`chip accent tn${stunden === h ? ' on' : ''}`} aria-pressed={stunden === h} style={{ height: 46, borderRadius: 12, padding: 0 }} onClick={() => setStunden(h)}>
                  {hours(h).replace(' h', '')}
                </button>
              ))}
            </div>
          </div>
          <label className="field">
            <span className="label">Tätigkeit</span>
            <input className="input" value={beschreibung} onChange={(e) => setBeschreibung(e.target.value)} maxLength={500} />
          </label>
          {error && <p className="error" role="alert">{error}</p>}
          <button className="btn lg block" onClick={save} disabled={busy}>{update.isPending ? 'Wird gespeichert …' : `${hours(stunden)} speichern`}</button>
          <div className="row" style={{ gap: 10 }}>
            <button className="btn secondary" style={{ flex: 1 }} onClick={() => onKopieren(leistung)} disabled={busy}><Icon name="plus" />Kopieren</button>
            <button className="btn secondary" style={{ flex: 1 }} onClick={del} disabled={busy}><Icon name="trash" />Löschen</button>
          </div>
        </>
      )}
    </Sheet>
  );
}
